// convert seconds into 00:00:00 format
const convertSecondsToTimeFormat = (seconds: number) =>
  new Date(seconds * 1000).toISOString().slice(11, 19);

type CameraTimezoneDescriptor = string | number | null | undefined;

type TimestampFormatOptions = {
  cameraTimezone?: CameraTimezoneDescriptor;
  includeMilliseconds?: boolean;
};

type EffectiveTimezone = {
  name: string;
  source: "camera" | "local";
};

type DateParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
  millisecond: number;
};

const parseTimezoneDescriptor = (
  value: unknown
): CameraTimezoneDescriptor => {
  if (typeof value === "number" && Number.isFinite(value)) {
    if (value >= -840 && value <= 840) return value;
    if (value >= -50400 && value <= 50400) return value / 60;
    return null;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const offsetMatch = trimmed.match(/^([+-])(\d{2}):?(\d{2})$/);
    if (offsetMatch) {
      const sign = offsetMatch[1] === "+" ? 1 : -1;
      return (
        sign *
        (parseInt(offsetMatch[2], 10) * 60 + parseInt(offsetMatch[3], 10))
      );
    }
    try {
      new Intl.DateTimeFormat("en-CA", { timeZone: trimmed });
      return trimmed;
    } catch {
      return null;
    }
  }
  return null;
};

const formatUtcOffsetLabel = (offsetMinutes: number) => {
  const sign = offsetMinutes >= 0 ? "+" : "-";
  const abs = Math.abs(offsetMinutes);
  const hours = Math.floor(abs / 60)
    .toString()
    .padStart(2, "0");
  const minutes = (abs % 60).toString().padStart(2, "0");
  return `UTC${sign}${hours}:${minutes}`;
};

const getViewerTimezoneName = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "local";
  } catch {
    return "local";
  }
};

const resolveEffectiveTimezone = (
  cameraTimezone: CameraTimezoneDescriptor
): EffectiveTimezone => {
  if (typeof cameraTimezone === "string" && cameraTimezone.length > 0) {
    return { name: cameraTimezone, source: "camera" };
  }
  if (typeof cameraTimezone === "number" && Number.isFinite(cameraTimezone)) {
    return { name: formatUtcOffsetLabel(cameraTimezone), source: "camera" };
  }
  return { name: getViewerTimezoneName(), source: "local" };
};

const getDatePartsInLocal = (date: Date): DateParts => ({
  year: date.getFullYear(),
  month: date.getMonth() + 1,
  day: date.getDate(),
  hour: date.getHours(),
  minute: date.getMinutes(),
  second: date.getSeconds(),
  millisecond: date.getMilliseconds(),
});

const getDatePartsInIanaZone = (
  date: Date,
  ianaName: string
): DateParts | null => {
  try {
    const formatter = new Intl.DateTimeFormat("en-CA", {
      timeZone: ianaName,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
    const parts = formatter.formatToParts(date);
    const getPart = (type: string) =>
      Number(parts.find((part) => part.type === type)?.value);

    return {
      year: getPart("year"),
      month: getPart("month"),
      day: getPart("day"),
      hour: getPart("hour"),
      minute: getPart("minute"),
      second: getPart("second"),
      millisecond: date.getUTCMilliseconds(),
    };
  } catch {
    return null;
  }
};

const getDatePartsForOffsetMinutes = (
  date: Date,
  offsetMinutes: number
): DateParts => {
  const shifted = new Date(date.getTime() + offsetMinutes * 60 * 1000);
  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
    hour: shifted.getUTCHours(),
    minute: shifted.getUTCMinutes(),
    second: shifted.getUTCSeconds(),
    millisecond: shifted.getUTCMilliseconds(),
  };
};

const resolveDateParts = (
  date: Date,
  cameraTimezone: CameraTimezoneDescriptor
): DateParts => {
  if (typeof cameraTimezone === "string" && cameraTimezone.length > 0) {
    const ianaParts = getDatePartsInIanaZone(date, cameraTimezone);
    if (ianaParts) return ianaParts;
  }
  if (typeof cameraTimezone === "number" && Number.isFinite(cameraTimezone)) {
    return getDatePartsForOffsetMinutes(date, cameraTimezone);
  }
  return getDatePartsInLocal(date);
};

const pad = (value: number, length = 2) =>
  `${value}`.padStart(length, "0");

const convertTimestampToDateTimeFormat = (
  timestampMs: number,
  options: TimestampFormatOptions = {}
): string => {
  const { includeMilliseconds = false, cameraTimezone = null } = options;
  const parts = resolveDateParts(new Date(timestampMs), cameraTimezone);
  const base = `${parts.year}-${pad(parts.month)}-${pad(parts.day)} ${pad(
    parts.hour
  )}:${pad(parts.minute)}:${pad(parts.second)}`;

  return includeMilliseconds ? `${base}.${pad(parts.millisecond, 3)}` : base;
};

const convertSecondsToExportTimestampFormat = (
  seconds: number,
  exportStartTimestampMs: number,
  options: TimestampFormatOptions = {}
): string =>
  convertTimestampToDateTimeFormat(
    exportStartTimestampMs + seconds * 1000,
    options
  );

const getConvertedJSONFromBufferArray = (bufferArray: []) => {
  const encoder = new TextDecoder("utf-8");
  const uint8Array = new Uint8Array(bufferArray);
  const encodedBufferArray = encoder.decode(uint8Array);

  return JSON.parse(encodedBufferArray);
};

// Extract the camera timezone from the 'crec' box's JSON payload.  mp4box
// has no dedicated parser for 'crec', so it stores the raw payload bytes on
// mp4BoxFile.crec.data, same as it does for udta's unrecognised children.
// Returns null if the box is absent, malformed, or carries no recognisable
// timezone value.
const readCrecBoxTimezone = (mp4BoxFile: any): CameraTimezoneDescriptor => {
  const data = mp4BoxFile?.crec?.data;
  if (!data) return null;
  try {
    const json = JSON.parse(new TextDecoder().decode(data));
    return parseTimezoneDescriptor(json?.timezone ?? null);
  } catch {
    return null;
  }
};

export {
  convertSecondsToExportTimestampFormat,
  convertSecondsToTimeFormat,
  convertTimestampToDateTimeFormat,
  getConvertedJSONFromBufferArray,
  parseTimezoneDescriptor,
  readCrecBoxTimezone,
  resolveEffectiveTimezone,
};
export type { CameraTimezoneDescriptor, EffectiveTimezone };
